import categoryService from '../services/categoryService';

/**
 * Generic handler for category service responses
 * @param {Function} serviceMethod - The service method to call
 * @returns {Promise<Object>} Standardized response object
 */
const handleCategoryResponse = async (serviceMethod) => {
    try {
        const response = await serviceMethod();
        return response.success 
            ? { success: true, data: response.data }
            : { success: false, error: response.message };
    } catch (error) {
        return { success: false, error: error.message };
    }
};

/**
 * Format number for display
 * @param {number} count - The count to format
 * @returns {string} Formatted count string
 */
const formatCount = (count) => {
    return count >= 1000 
        ? `${(count / 1000).toFixed(1)}K` 
        : count.toString();
};

/**
 * Capitalize first letter of a string
 * @param {string} str - String to capitalize
 * @returns {string} Capitalized string
 */
const capitalize = (str) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Fetch all categories with subcategories
 * @returns {Promise<Object>} Promise that resolves to response object
 */
export const fetchAllCategoriesWithSub = async () => {
    return handleCategoryResponse(() => categoryService.getAllCategoriesWithSub());
};

/**
 * Fetch all categories
 * @returns {Promise<Object>} Promise that resolves to response object
 */
export const fetchAllCategories = async () => {
    return handleCategoryResponse(() => categoryService.getAllCategories());
};

/**
 * Fetch categories with creator count
 * @param {boolean} isGuest - Whether the request is from a guest user
 * @returns {Promise<Object>} Promise that resolves to response object with raw categories data
 */
export const fetchCategoriesWithCount = async (isGuest = false) => {
    return handleCategoryResponse(() => categoryService.getCategoriesWithCount(isGuest));
};

/**
 * Fetch categories with creator count and format the data
 * @param {boolean} isGuest - Whether the request is from a guest user
 * @returns {Promise<Object>} Promise that resolves to response object with formatted categories data
 */
export const fetchFormattedCategoriesWithCount = async (isGuest = false) => {
    try {
        const response = await categoryService.getCategoriesWithCount(isGuest);
        
        if (!response.success) {
            return { success: false, error: response.message };
        }

        const formattedCategories = response.data.map(category => {
            const count = category.count;
            const countDisplay = formatCount(count);
            
            return {
                name: capitalize(category.category),
                count: count,
                creators: countDisplay,
                formattedCount: `${count >= 1000 ? countDisplay.toLowerCase() : count} creators`
            };
        });

        return { success: true, data: formattedCategories };
    } catch (error) {
        return { success: false, error: error.message };
    }
};

/**
 * Fetch category creators list with search and filters
 * @param {Object} params - Query parameters
 * @param {string} params.search - Search query string
 * @param {number} params.limit - Number of results to fetch
 * @param {string} params.userId - User ID
 * @returns {Promise<Object>} Promise that resolves to response object
 */
export const getCategoryCreatorsList = async (params = {}) => {
    return handleCategoryResponse(() => categoryService.getCategoryCreatorsList(params));
};